<template>
  <jd-popper ref="popperRef" :role="role">
    <jd-tooltip-trigger
      :disabled="disabled"
      :trigger="trigger"
      :trigger-keys="triggerKeys"
      :virtual-ref="virtualRef"
      :virtual-triggering="virtualTriggering"
    >
      <slot v-if="$slots.default" />
    </jd-tooltip-trigger>
    <jd-tooltip-content
      ref="contentRef"
      :aria-label="ariaLabel"
      :boundaries-padding="boundariesPadding"
      :content="content"
      :disabled="disabled"
      :effect="effect"
      :size="size"
      :enterable="enterable"
      :fallback-placements="fallbackPlacements"
      :hide-after="hideAfter"
      :gpu-acceleration="gpuAcceleration"
      :offset="offset"
      :persistent="persistent"
      :popper-class="popperClass"
      :popper-style="popperStyle"
      :placement="placement"
      :popper-options="popperOptions"
      :pure="pure"
      :raw-content="rawContent"
      :reference-el="referenceEl"
      :trigger-target-el="triggerTargetEl"
      :show-after="showAfter"
      :strategy="strategy"
      :teleported="teleported"
      :transition="transition"
      :virtual-triggering="virtualTriggering"
      :z-index="zIndex"
      :append-to="appendTo"
    >
      <slot name="content">
        <span v-if="rawContent" v-html="content" />
        <span v-else>{{ content }}</span>
      </slot>
      <jd-popper-arrow v-if="showArrow" :arrow-offset="arrowOffset" />
    </jd-tooltip-content>
  </jd-popper>
</template>

<script lang="ts" setup>
import {
  computed,
  inject,
  onDeactivated,
  provide,
  readonly,
  ref,
  toRef,
  unref,
  watch,
} from 'vue'
import { JdPopper, JdPopperArrow } from '@jdesign-vue/components/popper'

import { isBoolean } from '@jdesign-vue/utils'
import { useDelayedToggle, useId, usePopperContainer } from '@jdesign-vue/hooks'
import { TOOLTIP_INJECTION_KEY } from './constants'
import { tooltipEmits, useTooltipModelToggle, useTooltipProps } from './tooltip'
import JdTooltipTrigger from './trigger.vue'
import JdTooltipContent from './content.vue'
import type { PopperInstance } from '@jdesign-vue/components/popper'

defineOptions({
  name: 'JdTooltip',
})

const props = defineProps(useTooltipProps)
const emit = defineEmits(tooltipEmits)

const disableTrack = inject<boolean>('jdTooltipDisableTrack', false)

const dispatchTrack = (eventName: string) => {
  if (typeof document === 'undefined') return
  document.dispatchEvent(
    new CustomEvent('customTrack', {
      detail: {
        'data-component-name': 'Tooltip',
        'data-event-name': eventName,
        'data-event-content': props.content || props.ariaLabel || props.trigger,
      },
    })
  )
}

usePopperContainer()

const id = useId()
const popperRef = ref<PopperInstance>()
// TODO any is temporary, replace with `TooltipContentInstance` later
const contentRef = ref<any>()

const updatePopper = () => {
  const popperComponent = unref(popperRef)
  if (popperComponent) {
    popperComponent.popperInstanceRef?.update()
  }
}
const open = ref(false)
const toggleReason = ref<Event>()

const { show, hide, hasUpdateHandler } = useTooltipModelToggle({
  indicator: open,
  toggleReason,
})

const { onOpen, onClose } = useDelayedToggle({
  showAfter: toRef(props, 'showAfter'),
  hideAfter: toRef(props, 'hideAfter'),
  autoClose: toRef(props, 'autoClose'),
  open: show,
  close: hide,
})

const controlled = computed(
  () => isBoolean(props.visible) && !hasUpdateHandler.value
)

provide(TOOLTIP_INJECTION_KEY, {
  controlled,
  id,
  open: readonly(open),
  trigger: toRef(props, 'trigger'),
  onOpen: (event?: Event) => {
    onOpen(event)
  },
  onClose: (event?: Event) => {
    onClose(event)
  },
  onToggle: (event?: Event) => {
    if (unref(open)) {
      onClose(event)
    } else {
      onOpen(event)
    }
  },
  onShow: () => {
    if (!disableTrack) {
      dispatchTrack('open')
    }
    emit('show', toggleReason.value)
  },
  onHide: () => {
    if (!disableTrack) {
      dispatchTrack('close')
    }
    emit('hide', toggleReason.value)
  },
  onBeforeShow: () => {
    emit('before-show', toggleReason.value)
  },
  onBeforeHide: () => {
    emit('before-hide', toggleReason.value)
  },
  updatePopper,
})

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled && open.value) {
      open.value = false
    }
  }
)

const isFocusInsideContent = (event?: FocusEvent) => {
  const popperContent: HTMLElement | undefined =
    contentRef.value?.contentRef?.popperContentRef
  const activeElement = (event?.relatedTarget as Node) || document.activeElement

  return popperContent && popperContent.contains(activeElement)
}

onDeactivated(() => open.value && hide())

defineExpose({
  /**
   * @description jd-popper component instance
   */
  popperRef,
  /**
   * @description jd-tooltip-content component instance
   */
  contentRef,
  /**
   * @description validate current focus event is trigger inside jd-tooltip-content
   */
  isFocusInsideContent,
  /**
   * @description update jd-popper component instance
   */
  updatePopper,
  /**
   * @description expose onOpen function to mange jd-tooltip open state
   */
  onOpen,
  /**
   * @description expose onOpen function to mange jd-tooltip open state
   */
  onClose,
  /**
   * @description expose hide function
   */
  hide,
})
</script>
